import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {type AvatarProps, CounterLabel} from '@primer/react'
import {clsx} from 'clsx'
import type {ComponentPropsWithoutRef} from 'react'

import type {UserPresence} from '../api/alive/contracts'
import styles from './memex-avatar-stack.module.css'

export function itemFromPresence(presence: UserPresence & {avatarUrl: string}): MemexAvatarStackItem {
  return {
    ...presence,
    id: presence.userId,
    hovercardUrl: `/hovercards?user_id=${presence.userId}`,
  }
}

export type MemexAvatarStackItem = {
  id: number
  avatarUrl: string
  hovercardUrl: string
  isIdle?: boolean
}

type MemexAvatarStackProps = {
  items: ReadonlyArray<MemexAvatarStackItem>
  size?: number
  avatarColor?: string
  maxVisible?: number
}

type DivWrapperProps = ComponentPropsWithoutRef<'div'> & {ref?: React.Ref<HTMLDivElement>}

const MemexAvatarStackWrapper = ({ref, ...props}: DivWrapperProps) => {
  const {className, ...other} = props

  return <div className={clsx(styles.Box, className)} {...other} ref={ref} />
}

MemexAvatarStackWrapper.displayName = 'MemexAvatarStackWrapper'

const StyledAvatarBox = ({ref, ...props}: DivWrapperProps) => {
  const {className, ...other} = props

  return <div className={clsx(styles.Box_1, className)} {...other} ref={ref} />
}

StyledAvatarBox.displayName = 'StyledAvatarBox'

type StyledAvatarProps = Omit<AvatarProps, 'sx'> & {ref?: React.Ref<HTMLImageElement>}

const StyledAvatar = ({ref, ...props}: StyledAvatarProps) => {
  const {className, ...other} = props

  return <GitHubAvatar className={clsx(styles.GitHubAvatar, className)} {...other} ref={ref} />
}

StyledAvatar.displayName = 'StyledAvatar'

const StyledCounter = ({ref, ...props}: DivWrapperProps) => {
  const {className, ...other} = props

  return <div className={clsx(styles.Box_2, className)} {...other} ref={ref} />
}

StyledCounter.displayName = 'StyledCounter'

const MemexAvatarStack = ({items, avatarColor, size = 24, maxVisible = 5}: MemexAvatarStackProps) => {
  const count = items.length

  const overflowCount = count - maxVisible

  return (
    <MemexAvatarStackWrapper {...testIdProps('memex-avatar-stack')}>
      <div style={{'--avatar-background-color': avatarColor}} className={styles.Box_3}>
        {
          // NOTE: this is using an older hovercards endpoint that accepts a userId.  Once we start fetching users by id, we can use "/users/:username/hovercard"
        }
        {items.slice(0, maxVisible).map((user, index) => {
          return (
            <StyledAvatarBox
              key={user.id}
              {...testIdProps('memex-avatar-stack-item')}
              style={{
                zIndex: index,
              }}
              className={styles.StyledAvatarBox}
            >
              <StyledAvatar
                {...testIdProps(`memex-avatar-state-${user.isIdle ? 'idle' : 'active'}`)}
                size={size}
                className={clsx(user.isIdle ? styles.idleStyledAvatar : styles.StyledAvatar)}
                src={user.avatarUrl}
                data-hovercard-url={user.hovercardUrl}
              />
            </StyledAvatarBox>
          )
        })}
      </div>
      {count > maxVisible ? (
        <div
          style={{
            zIndex: maxVisible,
          }}
          className={styles.Box_4}
          {...testIdProps('memex-avatar-stack-overflow')}
        >
          <StyledCounter className={styles.StyledCounter}>
            <CounterLabel
              style={{
                '--label-size': `${size + 1}px`,
              }}
              className={clsx(styles.CounterLabel, size < 24 && styles.smallCounterLabel)}
            >
              +{overflowCount > 99 ? '∞' : overflowCount}
            </CounterLabel>
          </StyledCounter>
        </div>
      ) : null}
    </MemexAvatarStackWrapper>
  )
}

export default MemexAvatarStack

try{ MemexAvatarStack.displayName ||= 'MemexAvatarStack' } catch {}